import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api, fmtDate } from "@/lib/api";
import { ImageUpload, imageUrl } from "@/components/ImageUpload";
import { EventInfoFieldset, EventInfoDisplay } from "@/components/EventInfoFieldset";
import { MessageComposer } from "@/components/Messaging";
import { toast } from "sonner";
import { CalendarDays, MapPin, Mail, Pencil, Check, X, MessageSquare, Megaphone, ChevronDown, ChevronUp, Users, AlertTriangle, Clock, Star, ExternalLink, Globe, Linkedin, Instagram, Facebook, Twitter } from "lucide-react";

// Social platforms surfaced on the interested-vendor mini card. Order matters
// for the UI (LinkedIn first because it's the most B2B-credible signal for
// healthcare/wellness recruiting). Keys must match `vendor_profiles.social_links`.
const VENDOR_SOCIAL_LINKS = [
    { key: "linkedin",  icon: Linkedin,  label: "LinkedIn" },
    { key: "instagram", icon: Instagram, label: "Instagram" },
    { key: "facebook",  icon: Facebook,  label: "Facebook" },
    { key: "x",         icon: Twitter,   label: "X (Twitter)" },
];

/**
 * Compact vendor card surfaced inline next to each Interested/Invited
 * application so the host can size up the vendor at a glance — logo, primary
 * booth photo, a one-click jump to the full HealthFairX profile, and any
 * socials the vendor has filled in. Freebies / raffle / booth elements are
 * intentionally NOT repeated here — they're already captured verbatim in the
 * application notes block right below.
 */
const InterestedVendorCard = ({ vp }) => {
    if (!vp) return null;
    const photo = (vp.photos && vp.photos[0]) || null;
    const profileHref = vp.vendor_slug ? `/vendors/${vp.vendor_slug}` : (vp.id ? `/vendors/${vp.id}` : null);
    const socials = VENDOR_SOCIAL_LINKS.filter((s) => (vp.social_links || {})[s.key]);
    const website = vp.website || "";
    return (
        <div
            className="rounded-lg border border-brand-line bg-white p-3 flex items-center gap-3"
            data-testid={`vendor-card-${vp.id}`}
        >
            {vp.logo_url ? (
                <img
                    src={imageUrl(vp.logo_url)}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover border border-brand-line flex-shrink-0"
                    data-testid={`vendor-card-logo-${vp.id}`}
                />
            ) : (
                <div className="w-14 h-14 rounded-lg bg-brand-bone border border-brand-line flex items-center justify-center text-brand-mute font-heading font-bold flex-shrink-0">
                    {(vp.business_name || "?").slice(0, 1).toUpperCase()}
                </div>
            )}

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    {profileHref ? (
                        <a
                            href={profileHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-heading font-bold text-brand-ink hover:text-brand-forest inline-flex items-center gap-1"
                            data-testid={`vendor-card-profile-link-${vp.id}`}
                        >
                            View full profile <ExternalLink size={12} className="opacity-60" />
                        </a>
                    ) : (
                        <span className="font-heading font-bold text-brand-ink">{vp.business_name}</span>
                    )}
                </div>
                {(vp.home_city || vp.home_state) && (
                    <div className="text-xs text-brand-mute mt-0.5">
                        {[vp.home_city, vp.home_state].filter(Boolean).join(", ")}
                    </div>
                )}
                {(socials.length > 0 || website) && (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        {website && (
                            <a
                                href={website}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Website"
                                className="w-7 h-7 rounded-full border border-brand-line bg-white hover:bg-brand-mint inline-flex items-center justify-center text-brand-mute hover:text-brand-forest transition-colors"
                                data-testid={`vendor-card-website-${vp.id}`}
                            >
                                <Globe size={14} />
                            </a>
                        )}
                        {socials.map(({ key, icon: Icon, label }) => (
                            <a
                                key={key}
                                href={vp.social_links[key]}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={label}
                                className="w-7 h-7 rounded-full border border-brand-line bg-white hover:bg-brand-mint inline-flex items-center justify-center text-brand-mute hover:text-brand-forest transition-colors"
                                data-testid={`vendor-card-social-${key}-${vp.id}`}
                            >
                                <Icon size={14} />
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {photo && (
                <img
                    src={imageUrl(photo)}
                    alt=""
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border border-brand-line flex-shrink-0"
                    data-testid={`vendor-card-photo-${vp.id}`}
                />
            )}
        </div>
    );
};

export default function HostEventDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [ev, setEv] = useState(null);
    const [apps, setApps] = useState([]);
    const [cats, setCats] = useState([]);
    const [invite, setInvite] = useState({ vendor_email: "", category_id: "", message: "" });
    const [editing, setEditing] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);

    const load = async () => {
        const [e, a, c] = await Promise.all([
            api.get(`/events/${id}`),
            api.get(`/host/events/${id}/applications`),
            api.get("/categories"),
        ]);
        setEv(e.data); setApps(a.data); setCats(c.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const decide = async (appId, decision) => {
        try {
            await api.post(`/host/events/${id}/applications/${appId}/${decision}`);
            toast.success(decision === "approve" ? "Approved" : "Declined");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    };

    const submitInvite = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/host/events/${id}/invite`, { ...invite, event_id: id });
            toast.success("Invitation sent");
            setInvite({ vendor_email: "", category_id: "", message: "" });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not invite"); }
    };

    const patchEvent = async (updates) => {
        try {
            const res = await api.patch(`/events/${id}`, updates);
            setEv(res.data);
            setEditing(null);
            toast.success("Saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const messageVendor = async (vendorUserId) => {
        try {
            const res = await api.post(`/conversations/start`, { event_id: id, with_user_id: vendorUserId });
            nav(`/inbox`);
            // Hint Inbox to focus this conversation via hash if desired; for now just navigate.
            void res;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open conversation");
        }
    };

    const broadcast = async ({ body, attachments }) => {
        const res = await api.post(`/host/events/${id}/broadcast`, { body, attachments });
        toast.success(`Broadcast delivered to ${res.data.delivered} confirmed vendor${res.data.delivered === 1 ? "" : "s"}`);
        setBroadcastOpen(false);
    };

    if (!ev) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;
    const catName = (cid) => cats.find((c) => c.id === cid)?.name || cid;
    const hero = ev.images && ev.images[0];
    // Host-comped invites use status="confirmed" with host_comped=true and skip
    // payment. Count them alongside paid vendors as "confirmed".
    const confirmedCount = apps.filter((a) => ["paid", "confirmed", "attended"].includes(a.status)).length;
    const interestedCount = apps.filter((a) => ["interested", "invited"].includes(a.status)).length;

    return (
        <Shell>
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-6">
                {/* Compact header */}
                <div>
                    <Link to="/host" className="text-sm text-brand-mute hover:text-brand-forest">← All events</Link>
                    <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
                        <div className="flex items-center gap-4 min-w-0">
                            {hero && (
                                <img src={imageUrl(hero)} alt="" className="hidden sm:block h-16 w-24 rounded-lg object-cover border border-brand-line shrink-0" />
                            )}
                            <div className="min-w-0">
                                <span className="chip">{ev.status.replace("_", " ")}</span>
                                <h1 className="font-heading text-2xl md:text-3xl font-extrabold mt-1 truncate">{ev.name}</h1>
                                <div className="mt-1 text-xs text-brand-mute flex flex-wrap gap-3">
                                    <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(ev.event_date)} • {ev.start_time}–{ev.end_time}</span>
                                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            {ev.status !== "cancelled" && (
                                <>
                                    <button data-testid="reschedule-event-btn" onClick={() => setRescheduleOpen(true)} className="pill px-4 h-9 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">Reschedule</button>
                                    <button data-testid="cancel-event-btn" onClick={() => setCancelOpen(true)} className="pill px-4 h-9 text-sm font-semibold text-brand-coral border border-brand-coral/40 bg-white hover:bg-brand-coral/5">Cancel event</button>
                                </>
                            )}
                            <Link to={`/events/${ev.event_slug || ev.id}`} className="text-sm font-semibold text-brand-forest hover:underline">Public preview ↗</Link>
                        </div>
                    </div>
                    {ev.status === "cancelled" && (
                        <div className="mt-3 rounded-lg border border-brand-coral/30 bg-brand-coral/5 p-3 text-sm text-brand-ink" data-testid="event-cancelled-banner">
                            <strong>This event is cancelled.</strong> {ev.cancel_reason ? `Reason: ${ev.cancel_reason}` : ""} All paid vendors have been refunded in full.
                        </div>
                    )}
                    {ev.reschedule_grace_active && ev.status !== "cancelled" && (
                        <div className="mt-3 rounded-lg border border-brand-line bg-brand-mint/40 p-3 text-sm text-brand-ink" data-testid="event-rescheduled-banner">
                            This event has been rescheduled. Confirmed vendors who choose to cancel will receive a full refund regardless of timing.
                        </div>
                    )}
                </div>

                {/* Post-event: review vendors */}
                <PostEventReviews event={ev} />

                {/* Vendor Pipeline — top of page, above the fold */}
                <VendorMixDashboard eventId={id} reloadKey={apps.length} />

                <div className="rounded-2xl border border-brand-line bg-white p-6" data-testid="vendor-pipeline">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <h2 className="font-heading text-xl font-extrabold">Vendor pipeline</h2>
                            <p className="text-xs text-brand-mute mt-0.5">
                                {apps.length} total · {interestedCount} interested · <span className="text-emerald-700 font-semibold">{confirmedCount} confirmed</span>
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button data-testid="broadcast-open" onClick={() => setBroadcastOpen(true)} disabled={confirmedCount === 0}
                                className="pill btn-coral px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                                <Megaphone size={14} /> Broadcast to confirmed ({confirmedCount})
                            </button>
                        </div>
                    </div>
                    {apps.length === 0 ? (
                        <p className="text-brand-mute text-sm py-6">No applications yet. Once your event is published, eligible vendors nearby will be notified.</p>
                    ) : (
                        <ul className="divide-y divide-brand-line">
                            {apps.map((a) => (
                                <li key={a.id} className="py-3 space-y-2" data-testid={`app-row-${a.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold truncate">{a.vendor?.business_name || "Vendor"}</div>
                                            <div className="text-xs text-brand-mute">{catName(a.category_id)} · status: <span className="font-semibold text-brand-ink">{a.status.replace(/_/g, " ")}</span></div>
                                        </div>
                                        <div className="flex gap-2 flex-wrap justify-end">
                                            {(a.status === "interested" || a.status === "invited") && (
                                                <>
                                                    <button data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approve")} className="pill btn-forest px-3 py-1.5 text-xs font-semibold">Approve</button>
                                                    <button data-testid={`decline-${a.id}`} onClick={() => decide(a.id, "decline")} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone">Decline</button>
                                                    <button
                                                        data-testid={`message-${a.id}`}
                                                        onClick={() => messageVendor(a.vendor_id)}
                                                        title="Ask the vendor for more details before approving"
                                                        className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/30 text-brand-forest bg-white hover:bg-brand-mint inline-flex items-center gap-1"
                                                    >
                                                        <MessageSquare size={12} /> Message
                                                    </button>
                                                </>
                                            )}
                                            {a.status === "approved_pending_payment" && <span className="text-xs text-amber-700 bg-amber-100 px-2.5 py-1 rounded-md font-semibold">Awaiting confirmation</span>}
                                            {(a.status === "paid" || a.status === "confirmed" || a.status === "attended") && (
                                                <>
                                                    <span className="text-xs text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-md font-semibold">
                                                        {a.host_comped ? "Confirmed (comp)" : "Confirmed"}
                                                    </span>
                                                    <button data-testid={`message-${a.id}`} onClick={() => messageVendor(a.vendor_id)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/30 text-brand-forest bg-white hover:bg-brand-mint inline-flex items-center gap-1">
                                                        <MessageSquare size={12} /> Message
                                                    </button>
                                                </>
                                            )}
                                            {a.status === "declined" && <span className="text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md font-semibold">Declined</span>}
                                        </div>
                                    </div>
                                    {(a.status === "interested" || a.status === "invited") && (
                                        <InterestedVendorCard vp={a.vendor} />
                                    )}
                                    {a.notes && (
                                        <div className="rounded-lg bg-brand-bone/60 border border-brand-line px-3 py-2.5 text-sm text-brand-ink whitespace-pre-line" data-testid={`app-notes-${a.id}`}>
                                            <div className="text-[10px] uppercase tracking-wider font-bold text-brand-mute mb-1">Vendor pitch</div>
                                            {a.notes}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Invite */}
                <div className="rounded-xl border border-brand-line bg-white p-6">
                    <h3 className="font-heading font-bold flex items-center gap-2"><Mail size={16} className="text-brand-coral" /> Invite a vendor directly</h3>
                    <p className="text-sm text-brand-mute mt-1">Have a vendor in mind? Send them an invitation by email — they'll be <strong className="text-brand-ink">automatically confirmed</strong> for this event, no application or payment required.</p>
                    <div className="mt-3 rounded-lg border border-brand-coral/30 bg-brand-coral/5 p-3 flex items-start gap-2" data-testid="invite-disclaimer">
                        <AlertTriangle size={14} className="text-brand-coral mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-brand-ink leading-relaxed">
                            <strong>Please use sparingly.</strong> HealthFairX is free for hosts because we charge vendors a small booth fee. Any vendor you invite directly via this tool bypasses the normal Express Interest + payment flow and is confirmed at no cost to them.
                        </p>
                    </div>
                    <form onSubmit={submitInvite} className="mt-4 grid md:grid-cols-3 gap-3" data-testid="invite-form">
                        <input data-testid="invite-email" required type="email" placeholder="vendor@example.com" className="input-base" value={invite.vendor_email} onChange={(e) => setInvite({ ...invite, vendor_email: e.target.value })} />
                        <select data-testid="invite-cat" required className="input-base" value={invite.category_id} onChange={(e) => setInvite({ ...invite, category_id: e.target.value })}>
                            <option value="">Category…</option>
                            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button data-testid="invite-submit" type="submit" className="pill btn-coral h-11 font-semibold">Send invite & comp booth</button>
                        <textarea data-testid="invite-msg" placeholder="Personal note (optional)" rows={2} className="input-base !h-auto py-3 md:col-span-3" value={invite.message} onChange={(e) => setInvite({ ...invite, message: e.target.value })} />
                    </form>
                </div>

                {/* Event details — collapsible */}
                <div className="rounded-2xl border border-brand-line bg-white" data-testid="event-details">
                    <button onClick={() => setDetailsOpen((v) => !v)} className="w-full flex items-center justify-between px-6 py-4 text-left">
                        <div>
                            <h2 className="font-heading text-xl font-extrabold">Event details</h2>
                            <p className="text-xs text-brand-mute mt-0.5">Edit basics, date, location, capacity, logistics, categories and images.</p>
                        </div>
                        {detailsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    {detailsOpen && (
                        <div className="border-t border-brand-line p-6 space-y-4">
                            <EditableCard title="Basics" onEdit={() => setEditing("basics")} editing={editing === "basics"} onCancel={() => setEditing(null)}
                                render={() => (
                                    <div className="space-y-2 text-sm">
                                        <div><span className="text-brand-mute">Name:</span> <span className="font-semibold">{ev.name}</span></div>
                                        <div><span className="text-brand-mute">Description:</span><div className="mt-1 whitespace-pre-line">{ev.description}</div></div>
                                    </div>
                                )}
                                form={(close) => <BasicsForm ev={ev} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Date & time" onEdit={() => setEditing("date")} editing={editing === "date"} onCancel={() => setEditing(null)}
                                render={() => <div className="text-sm">{fmtDate(ev.event_date)} • {ev.start_time} – {ev.end_time}</div>}
                                form={(close) => <DateForm ev={ev} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Location" onEdit={() => setEditing("loc")} editing={editing === "loc"} onCancel={() => setEditing(null)}
                                render={() => (
                                    <div className="text-sm">
                                        <div className="font-semibold">{ev.location}</div>
                                        <div className="text-brand-mute">{[ev.city, ev.state].filter(Boolean).join(", ") || "—"}</div>
                                        {ev.geocoded_address && <div className="text-xs text-brand-mute mt-1">📍 {ev.geocoded_address}</div>}
                                        {ev.lat && ev.lng && <div className="text-xs text-brand-mute">Coordinates: {ev.lat.toFixed(4)}, {ev.lng.toFixed(4)} — used to notify nearby vendors automatically.</div>}
                                    </div>
                                )}
                                form={(close) => <LocationForm ev={ev} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Capacity" onEdit={() => setEditing("cap")} editing={editing === "cap"} onCancel={() => setEditing(null)}
                                render={() => <div className="text-sm">{ev.total_booth_capacity} booths</div>}
                                form={(close) => <CapacityForm ev={ev} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Insurance & venue details" onEdit={() => setEditing("info")} editing={editing === "info"} onCancel={() => setEditing(null)}
                                render={() => {
                                    const v = ev.event_info || {};
                                    const hasAny = Object.values(v).some((x) => x != null && x !== "");
                                    return hasAny
                                        ? <EventInfoDisplay value={v} totalEmployees={ev.total_employees} />
                                        : <div className="text-sm text-brand-mute">Not set yet. Add insurance carriers, workforce composition, and venue logistics to help vendors prep.</div>;
                                }}
                                form={(close) => <EventInfoForm ev={ev} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Parking & arrival" onEdit={() => setEditing("park")} editing={editing === "park"} onCancel={() => setEditing(null)}
                                render={() => <div className="text-sm whitespace-pre-line">{ev.parking_instructions || <span className="text-brand-mute">Not set yet.</span>}</div>}
                                form={(close) => <TextareaForm field="parking_instructions" initial={ev.parking_instructions || ""} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Setup & logistics" onEdit={() => setEditing("setup")} editing={editing === "setup"} onCancel={() => setEditing(null)}
                                render={() => <div className="text-sm whitespace-pre-line">{ev.setup_instructions || <span className="text-brand-mute">Not set yet.</span>}</div>}
                                form={(close) => <TextareaForm field="setup_instructions" initial={ev.setup_instructions || ""} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <EditableCard title="Vendor categories & capacity" onEdit={() => setEditing("cats")} editing={editing === "cats"} onCancel={() => setEditing(null)}
                                render={() => {
                                    const rows = ev.category_controls || [];
                                    if (rows.length === 0) return <span className="text-sm text-brand-mute">None selected.</span>;
                                    return (
                                        <div className="flex flex-wrap gap-2">
                                            {rows.map((r) => (
                                                <span key={r.category_id} className="chip">
                                                    {catName(r.category_id)} <span className="text-brand-mute font-normal ml-1">({r.max_count || 1})</span>
                                                </span>
                                            ))}
                                        </div>
                                    );
                                }}
                                form={(close) => <CategoriesForm ev={ev} cats={cats} onSave={(u) => patchEvent(u)} onCancel={close} />}
                            />
                            <div className="rounded-xl border border-brand-line bg-white p-6">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="font-heading font-bold">Event images</h3>
                                </div>
                                <p className="text-sm text-brand-mute mb-4">The first image is used as the hero on the public event page and marketplace card.</p>
                                <ImageUpload
                                    value={ev.images || []}
                                    onChange={(images) => patchEvent({ images })}
                                    max={6}
                                    label="Photos"
                                    testIdPrefix="host-ev-img"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Broadcast modal */}
            {broadcastOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="broadcast-modal">
                    <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-auto">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="label-base flex items-center gap-2"><Megaphone size={14} className="text-brand-coral" /> Broadcast</div>
                                <h2 className="font-heading text-2xl font-extrabold mt-1">Message all confirmed vendors</h2>
                                <p className="text-sm text-brand-mute mt-1">This will send to <span className="font-semibold text-brand-ink">{confirmedCount}</span> confirmed vendor{confirmedCount === 1 ? "" : "s"} for "{ev.name}". They'll see it in their inbox tagged as a Broadcast and receive an email notification.</p>
                            </div>
                            <button onClick={() => setBroadcastOpen(false)} className="text-brand-mute hover:text-brand-ink"><X size={18} /></button>
                        </div>
                        <MessageComposer onSend={broadcast} placeholder="Write your broadcast (e.g. updated parking instructions, day-of arrival times)…" testIdPrefix="broadcast" />
                    </div>
                </div>
            )}
            {rescheduleOpen && (
                <RescheduleEventModal
                    event={ev}
                    onClose={() => setRescheduleOpen(false)}
                    onDone={async () => { setRescheduleOpen(false); await load(); }}
                />
            )}
            {cancelOpen && (
                <CancelEventModal
                    event={ev}
                    onClose={() => setCancelOpen(false)}
                    onDone={async () => { setCancelOpen(false); await load(); }}
                />
            )}
        </Shell>
    );
}

const EditableCard = ({ title, render, form, editing, onEdit, onCancel }) => (
    <div className="rounded-xl border border-brand-line bg-white p-6">
        <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold">{title}</h3>
            {!editing ? (
                <button onClick={onEdit} data-testid={`edit-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm font-semibold text-brand-forest inline-flex items-center gap-1 hover:underline">
                    <Pencil size={14} /> Edit
                </button>
            ) : (
                <button onClick={onCancel} className="text-sm font-semibold text-brand-mute inline-flex items-center gap-1 hover:text-brand-ink">
                    <X size={14} /> Cancel
                </button>
            )}
        </div>
        {editing ? form(onCancel) : render()}
    </div>
);

const SaveBar = ({ onSave, onCancel, disabled }) => (
    <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onCancel} className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">Cancel</button>
        <button type="submit" disabled={disabled} onClick={onSave} className="pill btn-coral px-5 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1"><Check size={14} /> Save</button>
    </div>
);

const BasicsForm = ({ ev, onSave, onCancel }) => {
    const [name, setName] = useState(ev.name);
    const [description, setDescription] = useState(ev.description);
    return (
        <div className="space-y-3">
            <div>
                <label className="label-base">Name</label>
                <input data-testid="edit-name" className="input-base" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
                <label className="label-base">Description</label>
                <textarea data-testid="edit-description" rows={4} className="input-base !h-auto py-3" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <SaveBar onCancel={onCancel} onSave={() => onSave({ name, description })} />
        </div>
    );
};

const DateForm = ({ ev, onSave, onCancel }) => {
    const [d, setD] = useState({ event_date: ev.event_date || "", start_time: ev.start_time || "", end_time: ev.end_time || "" });
    return (
        <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="label-base">Date</label><input data-testid="edit-date-date" type="date" className="input-base" value={d.event_date} onChange={(e) => setD({ ...d, event_date: e.target.value })} /></div>
                <div><label className="label-base">Start</label><input data-testid="edit-date-start" type="time" className="input-base" value={d.start_time} onChange={(e) => setD({ ...d, start_time: e.target.value })} /></div>
                <div><label className="label-base">End</label><input data-testid="edit-date-end" type="time" className="input-base" value={d.end_time} onChange={(e) => setD({ ...d, end_time: e.target.value })} /></div>
            </div>
            <SaveBar onCancel={onCancel} onSave={() => onSave(d)} />
        </div>
    );
};

const LocationForm = ({ ev, onSave, onCancel }) => {
    const [d, setD] = useState({ location: ev.location || "", city: ev.city || "", state: ev.state || "" });
    return (
        <div className="space-y-3">
            <div>
                <label className="label-base">Venue / address</label>
                <input data-testid="edit-loc-address" className="input-base" value={d.location} onChange={(e) => setD({ ...d, location: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="label-base">City</label><input data-testid="edit-loc-city" className="input-base" value={d.city} onChange={(e) => setD({ ...d, city: e.target.value })} /></div>
                <div><label className="label-base">State</label><input data-testid="edit-loc-state" className="input-base" placeholder="e.g. VA" value={d.state} onChange={(e) => setD({ ...d, state: e.target.value })} /></div>
            </div>
            <p className="text-xs text-brand-mute">Saving re-geocodes this address to its latitude/longitude so nearby vendors get notified automatically.</p>
            <SaveBar onCancel={onCancel} onSave={() => onSave(d)} />
        </div>
    );
};

const CapacityForm = ({ ev, onSave, onCancel }) => {
    const [v, setV] = useState(ev.total_booth_capacity || 10);
    const [emp, setEmp] = useState(ev.total_employees ?? "");
    return (
        <div className="space-y-3">
            <div>
                <label className="label-base">Total booth capacity</label>
                <input data-testid="edit-capacity" type="number" min={1} className="input-base max-w-xs" value={v} onChange={(e) => setV(parseInt(e.target.value || "0", 10))} />
                <p className="text-xs text-brand-mute mt-1.5">How many vendor booths you have space for.</p>
            </div>
            <div>
                <label className="label-base">Total employees at this location</label>
                <input data-testid="edit-total-employees" type="number" min={0} className="input-base max-w-xs" placeholder="e.g. 1200" value={emp} onChange={(e) => setEmp(e.target.value.replace(/[^\d]/g, ""))} />
                <p className="text-xs text-brand-mute mt-1.5">Total workforce at the worksite. Helps vendors estimate booth traffic.</p>
            </div>
            <SaveBar onCancel={onCancel} onSave={() => onSave({
                total_booth_capacity: v,
                total_employees: emp === "" ? null : parseInt(emp, 10),
            })} />
        </div>
    );
};

const TextareaForm = ({ field, initial, onSave, onCancel }) => {
    const [v, setV] = useState(initial);
    return (
        <div className="space-y-3">
            <textarea data-testid={`edit-${field}`} rows={4} className="input-base !h-auto py-3" value={v} onChange={(e) => setV(e.target.value)} />
            <SaveBar onCancel={onCancel} onSave={() => onSave({ [field]: v })} />
        </div>
    );
};

// Inline editor for the nested event_info object. Pre-populates with the
// current value so partial edits (e.g. just toggling Wi-Fi) don't blow
// away the rest of the fields.
const EventInfoForm = ({ ev, onSave, onCancel }) => {
    const [info, setInfo] = useState(ev.event_info || {});
    return (
        <div className="space-y-4">
            <EventInfoFieldset value={info} onChange={setInfo} testidPrefix="host-edit-event-info" />
            <SaveBar onCancel={onCancel} onSave={() => onSave({ event_info: info })} />
        </div>
    );
};

const CategoriesForm = ({ ev, cats, onSave, onCancel }) => {
    // Initial state: pull from category_controls (source of truth). Fall back
    // to legacy desired_categories for grandfathered events with no controls.
    const seed = () => {
        if (Array.isArray(ev.category_controls) && ev.category_controls.length) {
            return ev.category_controls.map((r) => ({
                category_id: r.category_id,
                max_count: r.max_count || 1,
                desired_count: r.desired_count || r.max_count || 1,
                waitlist_enabled: r.waitlist_enabled !== false,
                requires_approval_after_cap: r.requires_approval_after_cap !== false,
            }));
        }
        return (ev.desired_categories || []).map((cid) => ({
            category_id: cid, max_count: 1, desired_count: 1,
            waitlist_enabled: true, requires_approval_after_cap: true,
        }));
    };
    const [rows, setRows] = useState(seed);

    const toggle = (id) => {
        const has = rows.some((r) => r.category_id === id);
        setRows(has
            ? rows.filter((r) => r.category_id !== id)
            : [...rows, { category_id: id, max_count: 1, desired_count: 1, waitlist_enabled: true, requires_approval_after_cap: true }]
        );
    };
    const updateRow = (cid, patch) => setRows(rows.map((r) => r.category_id === cid ? { ...r, ...patch } : r));

    return (
        <div className="space-y-4">
            <div>
                <p className="text-xs text-brand-mute mb-2">Click to add or remove categories.</p>
                <div className="flex flex-wrap gap-2">
                    {cats.map((c) => {
                        const active = rows.some((r) => r.category_id === c.id);
                        return (
                            <button key={c.id} type="button" data-testid={`edit-cat-${c.id}`} onClick={() => toggle(c.id)}
                                className={`pill px-4 py-2 text-sm font-semibold border ${active ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-line hover:border-brand-forest"}`}>
                                {c.name}
                            </button>
                        );
                    })}
                </div>
            </div>
            {rows.length > 0 && (
                <div className="rounded-lg border border-brand-line overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-bone">
                            <tr className="text-left text-xs uppercase tracking-wider text-brand-mute">
                                <th className="px-3 py-2.5">Category</th>
                                <th className="px-2 py-2.5">Capacity</th>
                                <th className="px-2 py-2.5 text-center">Waitlist</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const cat = cats.find((c) => c.id === r.category_id);
                                return (
                                    <tr key={r.category_id} className="border-t border-brand-line" data-testid={`edit-vmrow-${r.category_id}`}>
                                        <td className="px-3 py-2 font-semibold">{cat?.name || r.category_id}</td>
                                        <td className="px-2 py-2">
                                            <input
                                                data-testid={`edit-vmrow-${r.category_id}-max`}
                                                type="number" min={1}
                                                className="input-base !h-9 w-20"
                                                value={r.max_count}
                                                onChange={(e) => {
                                                    const n = Math.max(1, parseInt(e.target.value || "1", 10));
                                                    updateRow(r.category_id, { max_count: n, desired_count: n });
                                                }}
                                            />
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                            <input
                                                type="checkbox" className="h-4 w-4"
                                                checked={r.waitlist_enabled}
                                                onChange={(e) => updateRow(r.category_id, { waitlist_enabled: e.target.checked })}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <SaveBar onCancel={onCancel} onSave={() => onSave({ category_controls: rows })} />
        </div>
    );
};

// ---- Vendor mix dashboard + waitlist viewer (Phase 2) -----------------------

const STATUS_META = {
    at_capacity: { label: "At capacity", color: "text-brand-coral bg-brand-coral/10 border-brand-coral/40" },
    approaching: { label: "Approaching", color: "text-amber-700 bg-amber-50 border-amber-300" },
    desired_met: { label: "Desired met", color: "text-emerald-800 bg-emerald-50 border-emerald-300" },
    open: { label: "Open", color: "text-brand-mute bg-brand-bone border-brand-line" },
};

const VendorMixDashboard = ({ eventId, reloadKey }) => {
    const { id } = useParams();
    void id;
    const [mix, setMix] = useState(null);
    const [waitlist, setWaitlist] = useState([]);
    const [openCat, setOpenCat] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [m, w] = await Promise.all([
                api.get(`/host/events/${eventId}/vendor-mix`),
                api.get(`/host/events/${eventId}/waitlist`),
            ]);
            setMix(m.data); setWaitlist(w.data);
        } catch (err) { console.warn("HostEventDetail: optional load failed (probably not the owner)", err); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId, reloadKey]);

    const waitlistFor = (catId) => waitlist.filter((w) => w.category_id === catId);

    const approve = async (entryId) => {
        setBusy(true);
        try {
            await api.post(`/host/events/${eventId}/waitlist/${entryId}/approve`);
            toast.success("Approved — vendor will pay to confirm");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const decline = async (entryId) => {
        if (!window.confirm("Remove this vendor from the waitlist?")) return;
        setBusy(true);
        try {
            await api.delete(`/host/events/${eventId}/waitlist/${entryId}`);
            toast.success("Removed from waitlist");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    if (!mix) return null;
    if (!mix.rows.length) return null; // no controls set up — hide entirely

    const { rows, totals } = mix;
    const atCap = rows.filter((r) => r.status === "at_capacity").length;
    const approaching = rows.filter((r) => r.status === "approaching").length;

    return (
        <div className="rounded-2xl border border-brand-line bg-white p-6 space-y-5" data-testid="vendor-mix-dashboard">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-heading text-xl font-extrabold">Vendor mix</h2>
                    <p className="text-sm text-brand-mute mt-1">
                        <span className="text-brand-ink">{totals.filled}</span> of <span className="text-brand-ink">{totals.max}</span> max slots filled across {rows.length} categor{rows.length === 1 ? "y" : "ies"}.
                        {" "}<span className="text-brand-ink">{totals.waitlist}</span> on waitlist.
                    </p>
                </div>
                <div className="flex gap-2">
                    {atCap > 0 && (
                        <span className="chip border-brand-coral/40 bg-brand-coral/10 text-brand-coral inline-flex items-center gap-1.5"><AlertTriangle size={12} /> {atCap} at capacity</span>
                    )}
                    {approaching > 0 && (
                        <span className="chip border-amber-300 bg-amber-50 text-amber-700 inline-flex items-center gap-1.5"><Clock size={12} /> {approaching} approaching</span>
                    )}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.open;
                    const pct = r.max_count > 0 ? Math.min(100, Math.round((r.filled_count / r.max_count) * 100)) : 0;
                    const expanded = openCat === r.category_id;
                    const wlFor = waitlistFor(r.category_id);
                    return (
                        <div key={r.category_id} className="rounded-xl border border-brand-line bg-brand-bone/40 p-4 flex flex-col" data-testid={`vmix-card-${r.category_id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="font-heading font-bold leading-tight">{r.category_name}</div>
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${meta.color}`}>{meta.label}</span>
                            </div>
                            <div className="mt-3 flex items-baseline gap-1.5">
                                <span className="font-heading text-3xl font-extrabold text-brand-forest">{r.filled_count}</span>
                                <span className="text-sm text-brand-mute">/ {r.max_count || "∞"} max{r.desired_count ? ` • ${r.desired_count} desired` : ""}</span>
                            </div>
                            <div className="mt-2 h-1.5 w-full rounded-full bg-white overflow-hidden">
                                <div className={`h-full ${r.status === "at_capacity" ? "bg-brand-coral" : r.status === "approaching" ? "bg-amber-500" : "bg-brand-forest"}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-2">
                                <div className="text-xs text-brand-mute">
                                    {r.waitlist_count > 0 ? (
                                        <span data-testid={`vmix-waitlist-count-${r.category_id}`}>{r.waitlist_count} on waitlist</span>
                                    ) : (
                                        <span className="italic">No waitlist</span>
                                    )}
                                </div>
                                {r.differs_from_default && (
                                    <span className="text-[10px] font-semibold text-brand-forest" title="Customized from platform default">⚙ custom</span>
                                )}
                            </div>
                            {r.waitlist_count > 0 && (
                                <button
                                    data-testid={`vmix-toggle-${r.category_id}`}
                                    type="button"
                                    onClick={() => setOpenCat(expanded ? null : r.category_id)}
                                    className="mt-3 text-xs font-semibold text-brand-forest hover:underline text-left"
                                >
                                    {expanded ? "Hide waitlist" : "View waitlist →"}
                                </button>
                            )}
                            {expanded && (
                                <div className="mt-3 space-y-2 border-t border-brand-line pt-3" data-testid={`vmix-waitlist-${r.category_id}`}>
                                    {wlFor.map((w) => (
                                        <div key={w.id} className="rounded-lg border border-brand-line bg-white p-3 space-y-2">
                                            <div className="flex items-center gap-3">
                                                {w.vendor.logo_url ? (
                                                    <img src={imageUrl(w.vendor.logo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />
                                                ) : (
                                                    <div className="h-9 w-9 rounded-full bg-brand-bone inline-flex items-center justify-center"><Users size={14} className="text-brand-mute" /></div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-sm truncate">{w.vendor.business_name || w.vendor.full_name || w.vendor.email}</div>
                                                    <div className="text-xs text-brand-mute truncate">{w.vendor.email}</div>
                                                </div>
                                                <button data-testid={`vmix-approve-${w.id}`} disabled={busy} onClick={() => approve(w.id)} className="pill btn-forest px-3 py-1.5 text-xs font-semibold disabled:opacity-50">Approve</button>
                                                <button data-testid={`vmix-decline-${w.id}`} disabled={busy} onClick={() => decline(w.id)} className="text-xs text-brand-coral hover:underline font-semibold">Remove</button>
                                            </div>
                                            {w.notes && (
                                                <div className="rounded-md bg-brand-bone/60 border border-brand-line px-3 py-2 text-xs text-brand-ink whitespace-pre-line" data-testid={`vmix-waitlist-notes-${w.id}`}>
                                                    <div className="text-[10px] uppercase tracking-wider font-bold text-brand-mute mb-1">Vendor pitch</div>
                                                    {w.notes}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------------- 
// Reschedule / Cancel event modals (host-side).
// ----------------------------------------------------------------------------- 
const RescheduleEventModal = ({ event, onClose, onDone }) => {
    const [form, setForm] = useState({
        event_date: event.event_date || "",
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        reason: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (form.reason.trim().length < 3) return toast.error("Tell vendors why you're rescheduling.");
        if (!form.event_date) return toast.error("Pick the new date.");
        setBusy(true);
        try {
            await api.post(`/host/events/${event.id}/reschedule`, form);
            toast.success("Event rescheduled. All interested vendors have been notified.");
            onDone();
        } catch (err) { toast.error(err?.response?.data?.detail || "Reschedule failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="reschedule-event-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8">
                <h2 className="font-heading text-2xl font-extrabold">Reschedule event</h2>
                <p className="text-sm text-brand-mute mt-1">{event.name}</p>
                <div className="mt-3 rounded-lg bg-brand-mint/40 p-3 text-xs text-brand-ink">
                    Confirmed vendors will be notified. Anyone who cancels because of the new date gets a <strong>full refund</strong> regardless of timing.
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="label-base">New date</label>
                        <input data-testid="reschedule-date-input" type="date" className="input-base" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Start time</label>
                        <input data-testid="resched-start" className="input-base" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="10:00" />
                    </div>
                    <div>
                        <label className="label-base">End time</label>
                        <input data-testid="resched-end" className="input-base" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} placeholder="15:00" />
                    </div>
                    <div className="col-span-2">
                        <label className="label-base">Reason (visible to vendors) <span className="text-brand-coral">*</span></label>
                        <textarea data-testid="resched-reason" rows={3} className="input-base !h-auto py-3" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} maxLength={1000} />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="reschedule-event-submit" disabled={busy} onClick={submit} className="pill btn-forest px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Reschedule"}</button>
                </div>
            </div>
        </div>
    );
};

const CancelEventModal = ({ event, onClose, onDone }) => {
    const [reason, setReason] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (reason.trim().length < 3) return toast.error("A reason is required.");
        if (confirmText !== "CANCEL") return toast.error("Type CANCEL to confirm.");
        setBusy(true);
        try {
            const res = await api.post(`/host/events/${event.id}/cancel`, { reason });
            const issued = (res.data.refunds || []).filter((r) => r.status === "issued").length;
            toast.success(`Event cancelled. ${issued} vendor refund${issued === 1 ? "" : "s"} issued.`);
            onDone();
        } catch (err) { toast.error(err?.response?.data?.detail || "Cancel failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cancel-event-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8">
                <h2 className="font-heading text-2xl font-extrabold text-brand-coral">Cancel this event</h2>
                <p className="text-sm text-brand-mute mt-1">{event.name}</p>
                <div className="mt-4 rounded-lg border border-brand-coral/30 bg-brand-coral/5 p-3 text-sm text-brand-ink">
                    <strong>This cannot be undone.</strong> All paid vendors will receive a <strong>100% refund</strong> via Stripe. Vendors in earlier stages (interested, approved, waitlisted) will be notified and marked cancelled.
                </div>
                <div className="mt-5">
                    <label className="label-base">Reason (visible to vendors and admin audit log) <span className="text-brand-coral">*</span></label>
                    <textarea data-testid="cancel-reason" rows={3} className="input-base !h-auto py-3" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
                </div>
                <div className="mt-4">
                    <label className="label-base">Type <span className="font-bold tracking-wide">CANCEL</span> to confirm</label>
                    <input data-testid="cancel-event-confirm-input" className="input-base" value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())} />
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Keep event</button>
                    <button data-testid="cancel-event-submit" disabled={busy || confirmText !== "CANCEL"} onClick={submit} className="pill px-5 py-3 font-semibold text-white bg-brand-coral hover:bg-brand-coral/90 disabled:opacity-50">{busy ? "Cancelling…" : "Cancel event"}</button>
                </div>
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------------- 
// Post-event reviews block — shows only after the event date has passed and
// the host has confirmed vendors who haven't been reviewed yet.
// ----------------------------------------------------------------------------- 
const TAG_OPTIONS = ["Professional", "Engaging", "On Time", "Popular with Attendees", "Easy to Work With", "Would Invite Again"];

const PostEventReviews = ({ event }) => {
    const [pending, setPending] = useState([]);
    const [reviewing, setReviewing] = useState(null);

    const load = async () => {
        try {
            const r = await api.get(`/host/events/${event.id}/reviewable-vendors`);
            setPending(r.data);
        } catch (err) { console.warn("reviewable-vendors load failed", err); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.id]);

    const isPast = (event.event_date || "") < new Date().toISOString().slice(0, 10);
    if (!isPast || event.status === "cancelled") return null;
    if (pending.length === 0) return null;
    return (
        <div className="rounded-2xl border border-brand-line bg-white p-6" data-testid="post-event-reviews">
            <h2 className="font-heading text-xl font-extrabold">Review your vendors</h2>
            <p className="text-sm text-brand-mute mt-1">Your event is in the books — leave a quick review for each vendor. Reviews help future hosts find the best partners (and reward great vendors).</p>
            <ul className="mt-4 divide-y divide-brand-line">
                {pending.map((v) => (
                    <li key={v.vendor_id} className="py-3 flex items-center justify-between gap-3" data-testid={`reviewable-${v.vendor_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                            {v.logo_url ? (
                                <img src={imageUrl(v.logo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />
                            ) : (
                                <div className="h-9 w-9 rounded-full bg-brand-bone" />
                            )}
                            <div className="min-w-0">
                                <div className="font-semibold truncate">{v.business_name}</div>
                                <div className="text-xs text-brand-mute truncate">{v.email}</div>
                            </div>
                        </div>
                        <button data-testid={`review-vendor-btn-${v.vendor_id}`} onClick={() => setReviewing(v)} className="pill btn-coral px-4 py-1.5 text-sm font-semibold">Leave review</button>
                    </li>
                ))}
            </ul>
            {reviewing && (
                <ReviewVendorModal
                    event={event}
                    vendor={reviewing}
                    onClose={() => setReviewing(null)}
                    onSubmitted={async () => { setReviewing(null); await load(); }}
                />
            )}
        </div>
    );
};

const ReviewVendorModal = ({ event, vendor, onClose, onSubmitted }) => {
    const [stars, setStars] = useState(0);
    const [text, setText] = useState("");
    const [tags, setTags] = useState([]);
    const [inviteAgain, setInviteAgain] = useState("");
    const [busy, setBusy] = useState(false);
    const toggleTag = (t) => setTags((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);
    const submit = async () => {
        if (stars < 1) return toast.error("Pick a star rating.");
        if (!inviteAgain) return toast.error("Tell us if you'd invite this vendor again.");
        setBusy(true);
        try {
            await api.post("/host/reviews", {
                event_id: event.id,
                vendor_id: vendor.vendor_id,
                stars, text, tags,
                would_invite_again: inviteAgain,
            });
            toast.success("Review submitted! It'll be public once an admin approves it.");
            onSubmitted();
        } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="review-vendor-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-auto">
                <h2 className="font-heading text-2xl font-extrabold">Review {vendor.business_name}</h2>
                <p className="text-sm text-brand-mute mt-1">{event.name} • {event.event_date}</p>
                <div className="mt-6">
                    <label className="label-base">Overall rating</label>
                    <div className="flex gap-1 mt-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setStars(n)} aria-label={`${n} stars`}>
                                <Star size={28} className={n <= stars ? "fill-amber-400 text-amber-400" : "text-brand-line"} />
                            </button>
                        ))}
                    </div>
                </div>
                <div className="mt-5">
                    <label className="label-base">Written review (optional)</label>
                    <textarea data-testid="review-text" rows={3} className="input-base !h-auto py-3" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
                </div>
                <div className="mt-5">
                    <label className="label-base">Tags (optional)</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {TAG_OPTIONS.map((t) => (
                            <button key={t} type="button" data-testid={`review-tag-${t.replace(/ /g, "-")}`} onClick={() => toggleTag(t)}
                                className={`pill px-3 py-1.5 text-xs font-semibold border ${tags.includes(t) ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-line"}`}>
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="mt-5">
                    <label className="label-base">Would you invite this vendor again?</label>
                    <div className="flex gap-2 mt-2">
                        {[["yes", "Yes"], ["maybe", "Maybe"], ["no", "No"]].map(([v, l]) => (
                            <button key={v} type="button" data-testid={`review-invite-${v}`} onClick={() => setInviteAgain(v)}
                                className={`pill px-4 py-2 text-sm font-semibold border ${inviteAgain === v ? (v === "yes" ? "bg-emerald-600 text-white border-emerald-600" : v === "no" ? "bg-brand-coral text-white border-brand-coral" : "bg-amber-500 text-white border-amber-500") : "bg-white text-brand-ink border-brand-line"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="review-submit" disabled={busy} onClick={submit} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Submitting…" : "Submit review"}</button>
                </div>
            </div>
        </div>
    );
};

